/**
 * **Which profile's saved servers this device is showing.**
 *
 * The owner's words: *"different user profiles in app.. should have own saved
 * list.. if no account also have own list.."* — so every persisted server list on
 * this device is held **per profile**, and the profile is the **cloud account**
 * (`authStore.cloudUser.id`). A device with no account is not "no profile": it is
 * its own bucket, the **device** bucket, which is exactly where every list written
 * before this module existed already lives (the bare storage keys).
 *
 * ── One id, four stores ─────────────────────────────────────────────────────
 *
 * The modules that keep a list beside the hub-session keys all read this one
 * value: `services/serverProfiles.ts` (`otto_server_profiles`),
 * `services/forgottenServers.ts` (`otto_forgotten_servers`),
 * `services/savedConnections.ts` (`otto_saved_connections`, and the Electron main
 * process's `saved-hubs.json`), and the credential keys it owns
 * (`otto_creds_<id>_…`). Each one asks {@link profileScopedKey} for its key, so
 * there is one spelling of "whose list is this" and no module has to know how a
 * profile is named.
 *
 * ── The key convention ──────────────────────────────────────────────────────
 *
 *      base key                 → the device bucket (no account)
 *      <base key>:<accountId>   → that account's bucket
 *
 * The bare-key half is deliberate, and it is the same convention
 * `stores/serverOrder.ts` already uses for the dashboard arrangement. It is also
 * what makes the migration a non-event: every key written before this module
 * existed is already the device bucket, so nothing is moved, rewritten or lost —
 * a signed-out device paints the list it always painted, and every account starts
 * with the buffer the cloud gives it (`GET /hubs`).
 *
 * ── Why the id is also persisted here ───────────────────────────────────────
 *
 * The launcher paints its cards **before any network call** (the whole point of
 * the buffers), and at boot `authStore.cloudUser` is still null until
 * `fetchCloudUser()` answers. Without a synchronous copy of the last known
 * account id, a signed-in launch would paint the device bucket first and flip to
 * the account bucket a moment later — visibly "losing" the account's list. So the
 * id is mirrored under {@link PROFILE_ID_STORAGE_KEY}, read at module load, and
 * written/cleared by the auth store's one identity setter. The mirror is never
 * trusted on its own: the auth store only adopts it when a cloud token exists.
 */
import { ref } from 'vue';

/**
 * The bucket name the no-account list is held under in the **Electron main
 * process's** JSON file and in IPC arguments. localStorage does not need it (the
 * bare key is the device bucket), but a JSON object key cannot be `null` and an
 * IPC argument should not be ambiguous, so the main process names that bucket
 * `device`.
 */
export const PROFILE_DEVICE = 'device';

/**
 * Where the last known cloud account id is mirrored so the scope is known
 * synchronously at boot. Written and cleared **only** by the auth store's
 * `setCloudUser()`.
 */
export const PROFILE_ID_STORAGE_KEY = 'cloudUserId';

/** A cloud account id, trimmed and lowercased — the spelling every key uses. */
function normalizeProfileId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

/**
 * The active profile, as this session knows it. `null` = the device bucket.
 * Reactive so a surface can repaint when the account changes.
 */
const activeProfile = ref<string | null>(null);

/**
 * Bumped whenever the scope actually changes. Surfaces that hold their own copy
 * of a list (the launcher's `savedConnections` ref, `accountHubs`) watch this
 * instead of the auth store, so they reload for any reason the identity changed
 * (OAuth sign-in, sign-out, a renewal, another renderer's `storage` event).
 */
export const profileScopeVersion = ref(0);

/** The active profile id, or `null` for the no-account (device) bucket. */
export function activeProfileId(): string | null {
  return activeProfile.value;
}

/**
 * The active profile's bucket name — `device` when nobody is signed in. This is
 * the spelling the Electron main process takes over IPC.
 */
export function activeProfileBucket(): string {
  return activeProfile.value ?? PROFILE_DEVICE;
}

/**
 * The storage key for one base key in the active profile — the bare key for the
 * device bucket, `base:accountId` for an account.
 */
export function profileScopedKey(base: string, profileId: string | null = activeProfile.value): string {
  return profileId ? `${base}:${profileId}` : base;
}

type ProfileScopeListener = (profileId: string | null) => void;

const listeners = new Set<ProfileScopeListener>();

/**
 * Run `listener` whenever the scope changes. Returns the unsubscribe. A store
 * module uses this to drop its in-memory copy and re-read its own storage, since
 * the rows of one profile must never be painted under another.
 *
 * A listener is deliberately called **after** the new id is in place, and its
 * failures are contained: one broken re-read must not stop the others (and must
 * never take a sign-out down with it).
 */
export function onProfileScopeChange(listener: ProfileScopeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Point this device at a profile (`null` = no account). Idempotent: the same id
 * twice does **nothing**, which matters because the auth store re-asserts the
 * identity on every `fetchCloudUser()` — a re-read there must not throw away the
 * buffers a listener already holds in memory.
 */
export function setActiveProfile(value: unknown): void {
  const next = normalizeProfileId(value);
  if (next === activeProfile.value) return;
  activeProfile.value = next;
  profileScopeVersion.value += 1;
  for (const listener of [...listeners]) {
    try {
      listener(next);
    } catch (error) {
      console.warn('[ProfileScope] a listener failed to follow the profile change:', error);
    }
  }
}

/**
 * The scope as this device persisted it — `null` unless the caller says a cloud
 * session exists (an id without a token is a leftover, not an identity). Called
 * by the auth store at creation, before anything paints.
 */
export function readPersistedProfileId(sessionExists: boolean): string | null {
  if (!sessionExists) return null;
  try {
    return typeof localStorage === 'undefined' ? null : normalizeProfileId(localStorage.getItem(PROFILE_ID_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Tests only: back to the device bucket with no listeners. */
export function resetProfileScopeForTests(): void {
  listeners.clear();
  activeProfile.value = null;
  profileScopeVersion.value = 0;
}
